import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Clock, CheckCircle, XCircle, Loader, RotateCcw, Download, Pause, Play, Trash2, CheckSquare, Square } from 'lucide-react';
import Card from '../components/Card';
import LoadingSpinner from '../components/LoadingSpinner';
import ErrorMessage from '../components/ErrorMessage';
import { jobsApi } from '../api/jobs';
import { useEffect, useMemo, useState } from 'react';
import Input from '../components/Input';
import Button from '../components/Button';
import { Link, useNavigate, useParams } from 'react-router-dom';
import type { AuditEvent, Job } from '../types';
import UserBadge from '../components/UserBadge';
import JobProgressBar from '../components/JobProgressBar';
import { formatRelativeTime } from '../utils/formatRelativeTime';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../contexts/useAuth';

export default function JobsPage() {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const { jobId: jobIdFromPath } = useParams();
  const [jobId, setJobId] = useState('');
  const [searchJobId, setSearchJobId] = useState('');
  const [downloading, setDownloading] = useState<'txt' | 'json' | 'docx' | null>(null);
  const [filterStatus, setFilterStatus] = useState<string | null>(null);
  const [selectedJobs, setSelectedJobs] = useState<Set<string>>(new Set());
  const queryClient = useQueryClient();
  const { toast } = useToast();

  useEffect(() => {
    const id = String(jobIdFromPath ?? '').trim();
    setSearchJobId(id);
    if (id) setJobId('');
  }, [jobIdFromPath]);

  const { data: job, isLoading, error } = useQuery({
    queryKey: ['job', searchJobId],
    queryFn: () => jobsApi.getById(searchJobId),
    enabled: !!searchJobId,
    // Автоматически обновляем каждые 3 секунды, если задача активна
    refetchInterval: (query) => {
      const data = query.state.data as Job | undefined;
      if (data && (data.status === 'processing' || data.status === 'queued' || data.status === 'paused')) {
        return 3000; // 3 секунды для активных задач
      }
      return false; // Не обновляем завершенные или провалившиеся задачи
    },
  });

  const { data: auditEvents, isLoading: auditLoading } = useQuery({
    queryKey: ['job', searchJobId, 'audit'],
    queryFn: () => jobsApi.getAudit(searchJobId, { limit: 200 }),
    enabled: !!searchJobId,
  });

  // Получаем список всех задач
  const { data: allJobs, isLoading: allJobsLoading } = useQuery({
    queryKey: ['jobs', 'all'],
    queryFn: () => jobsApi.getAll({ limit: 50 }),
    // Обновляем каждые 5 секунд, если есть активные задачи
    refetchInterval: (query) => {
      const data = query.state.data as Job[] | undefined;
      if (data && data.some(j => j.status === 'processing' || j.status === 'queued' || j.status === 'paused')) {
        return 5000; // 5 секунд для списка задач
      }
      return false; // Не обновляем, если все задачи завершены
    },
  });

  const retryMutation = useMutation({
    mutationFn: (id: string) => jobsApi.retry(id),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: ['job', id] });
      queryClient.invalidateQueries({ queryKey: ['jobs', 'all'] });
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
      toast('Задача перезапущена', 'success');
    },
    onError: () => toast('Не удалось перезапустить задачу', 'error'),
  });

  const pauseMutation = useMutation({
    mutationFn: (id: string) => jobsApi.pause(id),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: ['job', id] });
      queryClient.invalidateQueries({ queryKey: ['jobs', 'all'] });
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
      toast('Задача приостановлена', 'info');
    },
    onError: () => toast('Не удалось приостановить задачу', 'error'),
  });

  const resumeMutation = useMutation({
    mutationFn: (id: string) => jobsApi.resume(id),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: ['job', id] });
      queryClient.invalidateQueries({ queryKey: ['jobs', 'all'] });
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
      toast('Задача возобновлена', 'success');
    },
    onError: () => toast('Не удалось возобновить задачу', 'error'),
  });

  const removeMutation = useMutation({
    mutationFn: (id: string) => jobsApi.remove(id),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: ['job', id] });
      queryClient.invalidateQueries({ queryKey: ['jobs', 'all'] });
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
      toast('Задача удалена', 'info');
    },
    onError: () => toast('Не удалось удалить задачу', 'error'),
  });

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'failed':
        return <XCircle className="h-5 w-5 text-red-500" />;
      case 'processing':
        return <Loader className="h-5 w-5 text-blue-500 animate-spin" />;
      case 'paused':
        return <Pause className="h-5 w-5 text-orange-500" />;
      case 'cancelled':
        return <XCircle className="h-5 w-5 text-gray-500" />;
      default:
        return <Clock className="h-5 w-5 text-yellow-500" />;
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'queued':
        return 'В очереди';
      case 'processing':
        return 'Обрабатывается';
      case 'completed':
        return 'Завершено';
      case 'failed':
        return 'Ошибка';
      case 'paused':
        return 'Приостановлено';
      case 'cancelled':
        return 'Отменено';
      default:
        return status;
    }
  };

  const getActorLabel = (e: AuditEvent) => {
    if (e.actor_type === 'user') {
      return e.actor_email ? `${e.actor_email}${e.actor_role ? ` (${e.actor_role})` : ''}` : 'Пользователь';
    }
    if (e.actor_type === 'api_key') return 'API key';
    if (e.actor_type === 'system') return 'Система';
    return e.actor_type || '—';
  };

  const getDetailString = (details: Record<string, unknown>, key: string): string | null => {
    const v = details[key];
    return typeof v === 'string' ? v : null;
  };

  const getAuditLabel = (e: AuditEvent) => {
    const d = e.details || {};
    switch (e.action) {
      case 'job.created':
        return 'Создана задача';
      case 'job.created_from_upload':
        return 'Создана задача (загрузка архива)';
      case 'job.duplicate_upload_detected':
        return 'Дубликат загрузки (задача уже существует)';
      case 'job.requeued_from_duplicate_upload':
        return 'Перезапущена из-за дубликата загрузки';
      case 'job.retried':
        return `Перезапуск${getDetailString(d, 'from_status') ? ` (из ${getDetailString(d, 'from_status')})` : ''}`;
      case 'job.paused':
        return 'Приостановлена';
      case 'job.resumed':
        return 'Возобновлена';
      case 'job.cancelled':
        return 'Отменена';
      case 'job.result_downloaded':
        return `Скачан результат${getDetailString(d, 'format') ? ` (${getDetailString(d, 'format')})` : ''}`;
      case 'job.status_changed': {
        const from = getDetailString(d, 'from_status');
        const to = getDetailString(d, 'to_status');
        if (from && to) return `Статус: ${getStatusLabel(from)} → ${getStatusLabel(to)}`;
        return 'Изменение статуса';
      }
      case 'job.worker_stopped':
        return `Воркер остановил обработку${getDetailString(d, 'status') ? ` (${getDetailString(d, 'status')})` : ''}`;
      default:
        return e.action;
    }
  };

  const getCreatedBy = () => {
    const items = auditEvents || [];
    for (let i = items.length - 1; i >= 0; i--) {
      const e = items[i];
      if (e.action === 'job.created' || e.action === 'job.created_from_upload') return getActorLabel(e);
    }
    return null;
  };

  const getSeriesTitle = (j: Job) => {
    if (j.series_title && String(j.series_title).trim()) return String(j.series_title);
    if (j.series_id && String(j.series_id).trim()) return `Серия ${String(j.series_id).slice(0, 8)}…`;
    return 'Без серии';
  };

  const getChapterNo = (j: Job): number | null => {
    const direct = j.chapter_no;
    if (typeof direct === 'number' && Number.isFinite(direct)) return direct;

    const fromProgress = j.progress?.['chapter_no'];
    if (typeof fromProgress === 'number' && Number.isFinite(fromProgress)) return fromProgress;
    if (typeof fromProgress === 'string' && fromProgress.trim() && !Number.isNaN(Number(fromProgress))) {
      return Number(fromProgress);
    }
    return null;
  };

  const hasOutputFiles = (j: Job) => {
    const outputFiles = j.progress?.['outputFiles'];
    return !!outputFiles && typeof outputFiles === 'object';
  };

  const getProgressNumber = (j: Job, key: string): number | null => {
    const v = j.progress?.[key];
    if (typeof v === 'number' && Number.isFinite(v)) return v;
    if (typeof v === 'string' && v.trim() && !Number.isNaN(Number(v))) return Number(v);
    return null;
  };

  const getProgressString = (j: Job, key: string): string | null => {
    const v = j.progress?.[key];
    if (typeof v === 'string' && v.trim()) return v.trim();
    if (typeof v === 'number' && Number.isFinite(v)) return String(v);
    return null;
  };

  const getStepLabel = (step: string | null) => {
    switch ((step || '').toLowerCase()) {
      case 'extracting':
        return 'Распаковка архива';
      case 'ocr':
        return 'OCR (распознавание текста)';
      case 'translating':
        return 'Перевод';
      case 'exporting':
        return 'Экспорт результатов';
      case 'done':
        return 'Готово';
      case 'error':
        return 'Ошибка';
      default:
        return step || '—';
    }
  };

  type JobRuntimeModels = {
    ocr?: {
      label?: string;
      chatModel?: string;
      chatBaseUrl?: string;
    };
    translation?: {
      label?: string;
      chatModel?: string;
      chatBaseUrl?: string;
      embeddingsModel?: string;
    };
  };

  type JobRuntimeInfo = {
    models?: JobRuntimeModels;
    prompts?: {
      ocr?: string;
      translation?: string;
    };
  };

  const getRuntime = (j: Job): JobRuntimeInfo | null => {
    const runtime = j.progress?.['runtime'];
    return runtime && typeof runtime === 'object' ? (runtime as JobRuntimeInfo) : null;
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = jobId.trim();
    if (trimmed) {
      navigate(`/jobs/${trimmed}`);
    } else {
      navigate('/jobs');
    }
  };

  const download = async (format: 'txt' | 'json' | 'docx', jobIdToDownload?: string) => {
    const targetJobId = jobIdToDownload || job?.id;
    if (!targetJobId) return;
    setDownloading(format);
    try {
      const { blob, filename } = await jobsApi.downloadResult(targetJobId, format);
      
      // Check if blob is empty
      if (blob.size === 0) {
        toast('Файл пуст или не найден. Проверьте, что задача завершена и файл создан.', 'error');
        return;
      }
      
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error: unknown) {
      console.error('Download error:', error);
      const message =
        (error as { response?: { data?: unknown } } | undefined)?.response?.data && typeof (error as { response?: { data?: unknown } }).response?.data === 'string'
          ? String((error as { response?: { data?: unknown } }).response?.data)
          : (error instanceof Error ? error.message : String(error));
      toast(`Ошибка при скачивании: ${message || 'Неизвестная ошибка'}`, 'error');
    } finally {
      setDownloading(null);
    }
  };

  const handleRetry = (id: string) => {
    if (window.confirm('Перезапустить задачу?')) {
      retryMutation.mutate(id);
    }
  };

  const statusTabs: { label: string; value: string | null; color: string }[] = [
    { label: 'Все', value: null, color: 'bg-gray-200 text-gray-700 dark:bg-gray-600 dark:text-gray-200' },
    { label: 'В очереди', value: 'queued', color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-200' },
    { label: 'В работе', value: 'processing', color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200' },
    { label: 'Завершено', value: 'completed', color: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200' },
    { label: 'Ошибка', value: 'failed', color: 'bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-200' },
  ];

  const statusCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const j of allJobs ?? []) {
      counts[j.status] = (counts[j.status] || 0) + 1;
    }
    return counts;
  }, [allJobs]);

  const filteredJobs = useMemo(() => {
    if (!allJobs) return [];
    if (!filterStatus) return allJobs;
    return allJobs.filter(j => j.status === filterStatus);
  }, [allJobs, filterStatus]);

  const failedJobs = useMemo(() => (allJobs ?? []).filter(j => j.status === 'failed'), [allJobs]);

  const batchMutation = useMutation({
    mutationFn: ({ ids, action }: { ids: string[]; action: 'retry' | 'remove' | 'pause' | 'resume' }) =>
      jobsApi.batchAction(ids, action),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['jobs', 'all'] });
      const ok = data.results.filter(r => r.ok).length;
      const fail = data.results.filter(r => !r.ok).length;
      toast(`${variables.action}: ${ok} успешно${fail > 0 ? `, ${fail} с ошибкой` : ''}`, fail > 0 ? 'error' : 'success');
      setSelectedJobs(new Set());
    },
    onError: (err: any) => toast(err?.message || 'Ошибка batch-операции', 'error'),
  });

  const handleBatchAction = (action: 'retry' | 'remove' | 'pause' | 'resume') => {
    const ids = Array.from(selectedJobs);
    if (ids.length === 0) return;
    const labels: Record<string, string> = { retry: 'Перезапустить', remove: 'Удалить', pause: 'Приостановить', resume: 'Возобновить' };
    if (!window.confirm(`${labels[action]} ${ids.length} задач?`)) return;
    batchMutation.mutate({ ids, action });
  };

  const handleRetryAllFailed = () => {
    if (failedJobs.length === 0) return;
    if (!window.confirm(`Перезапустить все ${failedJobs.length} задач с ошибкой?`)) return;
    batchMutation.mutate({ ids: failedJobs.map(j => j.id), action: 'retry' });
  };

  const toggleJobSelection = (id: string) => {
    setSelectedJobs(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedJobs.size === filteredJobs.length) {
      setSelectedJobs(new Set());
    } else {
      setSelectedJobs(new Set(filteredJobs.map(j => j.id)));
    }
  };

  return (
    <div>
      <button
        type="button"
        onClick={() => navigate('/jobs')}
        className="text-left text-3xl font-bold text-gray-900 dark:text-white mb-6 hover:text-primary-700 dark:hover:text-primary-300 transition-colors"
        title="Перейти к списку задач"
      >
        Задачи
      </button>

      <Card className="mb-6">
        <form onSubmit={handleSearch} className="flex gap-4">
          <Input
            label="ID задачи"
            value={jobId}
            onChange={(e) => setJobId(e.target.value)}
            placeholder="Введите ID задачи"
            className="flex-1"
          />
          <div className="flex items-end">
            <Button type="submit">Найти</Button>
          </div>
        </form>
      </Card>

      {searchJobId && (
        <>
          {isLoading ? (
            <LoadingSpinner />
          ) : error ? (
            <ErrorMessage message="Задача не найдена" />
          ) : job ? (
            <Card title={`Задача — ${getSeriesTitle(job)}${getChapterNo(job) ? ` • Глава ${getChapterNo(job)}` : ''}`}>
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  {getStatusIcon(job.status)}
                  <span className="font-medium text-gray-900 dark:text-white">
                    Статус: {getStatusLabel(job.status)}
                  </span>
                </div>

                <div className="flex gap-2 flex-wrap">
                  {(job.status === 'processing' || job.status === 'queued') && (
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => {
                        if (window.confirm('Приостановить задачу?')) {
                          pauseMutation.mutate(job.id);
                        }
                      }}
                      disabled={pauseMutation.isPending}
                    >
                      <Pause className="h-4 w-4 mr-1" />
                      {pauseMutation.isPending ? 'Приостановка…' : 'Приостановить'}
                    </Button>
                  )}
                  {job.status === 'paused' && (
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => {
                        resumeMutation.mutate(job.id);
                      }}
                      disabled={resumeMutation.isPending}
                    >
                      <Play className="h-4 w-4 mr-1" />
                      {resumeMutation.isPending ? 'Возобновление…' : 'Возобновить'}
                    </Button>
                  )}
                  {(job.status === 'processing' || job.status === 'queued' || job.status === 'paused') && (
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => {
                        if (window.confirm('Удалить задачу? Это действие нельзя отменить.')) {
                          removeMutation.mutate(job.id);
                        }
                      }}
                      disabled={removeMutation.isPending}
                    >
                      <Trash2 className="h-4 w-4 mr-1" />
                      {removeMutation.isPending ? 'Удаление…' : 'Удалить'}
                    </Button>
                  )}
                  {job.status === 'failed' && (
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => handleRetry(job.id)}
                      disabled={retryMutation.isPending}
                    >
                      <RotateCcw className="h-4 w-4 mr-1" />
                      {retryMutation.isPending ? 'Перезапуск…' : 'Перезапустить'}
                    </Button>
                  )}
                  {job.status === 'completed' && hasOutputFiles(job) && (
                    <>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => download('txt')}
                        disabled={!!downloading}
                      >
                        <Download className="h-4 w-4 mr-1" />
                        {downloading === 'txt' ? 'Скачивание…' : 'TXT'}
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => download('json')}
                        disabled={!!downloading}
                      >
                        <Download className="h-4 w-4 mr-1" />
                        {downloading === 'json' ? 'Скачивание…' : 'JSON'}
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => download('docx')}
                        disabled={!!downloading}
                      >
                        <Download className="h-4 w-4 mr-1" />
                        {downloading === 'docx' ? 'Скачивание…' : 'DOCX'}
                      </Button>
                    </>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">ID задачи</p>
                    <p className="text-gray-900 dark:text-white font-mono text-sm">{job.id}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Telegram ID</p>
                    <p className="text-gray-900 dark:text-white">{job.telegram_id}</p>
                  </div>
                  {job.series_id && (
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Серия</p>
                      <Link
                        to={`/series/${job.series_id}`}
                        className="text-gray-900 dark:text-white hover:text-primary-600 dark:hover:text-primary-400"
                      >
                        {job.series_title || job.series_id}
                      </Link>
                    </div>
                  )}
                  {job.chapter_id && (
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Глава</p>
                      <p className="text-gray-900 dark:text-white">
                        {getChapterNo(job) ? `Глава ${getChapterNo(job)}` : job.chapter_id}
                      </p>
                    </div>
                  )}
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Создано</p>
                    <p className="text-gray-900 dark:text-white">
                      {formatRelativeTime(job.created_at)}
                      <span className="text-xs text-gray-400 dark:text-gray-500 ml-2">
                        ({new Date(job.created_at).toLocaleString('ru-RU')})
                      </span>
                    </p>
                  </div>
                  {(job.created_by_username || getCreatedBy()) && (
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Кем создано</p>
                      <p className="text-gray-900 dark:text-white flex items-center gap-2">
                        <UserBadge username={job.created_by_username} size="md" />
                        {!job.created_by_username && getCreatedBy()}
                      </p>
                    </div>
                  )}
                </div>

                {Object.keys(job.progress).length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Прогресс</p>
                    <div className="mb-4">
                      <JobProgressBar job={job} />
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                        <div className="space-y-2 text-sm">
                          <div className="flex items-center justify-between">
                            <span className="text-gray-500 dark:text-gray-300">Шаг</span>
                            <span className="text-gray-900 dark:text-white font-medium">
                              {getStepLabel(getProgressString(job, 'step'))}
                            </span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-gray-500 dark:text-gray-300">Готовность</span>
                            <span className="text-gray-900 dark:text-white font-medium">
                              {getProgressNumber(job, 'progress') ?? 0}%
                            </span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-gray-500 dark:text-gray-300">Страницы</span>
                            <span className="text-gray-900 dark:text-white font-medium">
                              {getProgressNumber(job, 'currentPage') ?? 0} / {getProgressNumber(job, 'totalPages') ?? getProgressNumber(job, 'pagesToProcess') ?? getProgressNumber(job, 'pagesFound') ?? 0}
                            </span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-gray-500 dark:text-gray-300">Блоков найдено</span>
                            <span className="text-gray-900 dark:text-white font-medium">
                              {getProgressNumber(job, 'chunksFound') ?? 0}
                            </span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-gray-500 dark:text-gray-300">Переведено</span>
                            <span className="text-gray-900 dark:text-white font-medium">
                              {getProgressNumber(job, 'translated') ?? 0}
                            </span>
                          </div>
                          {getProgressNumber(job, 'batch') !== null && (
                            <div className="flex items-center justify-between">
                              <span className="text-gray-500 dark:text-gray-300">Пакет</span>
                              <span className="text-gray-900 dark:text-white font-medium">
                                {getProgressNumber(job, 'batch')}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>

                      {isAdmin && (
                      <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                        <p className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-3">
                          Используемые модели и промты
                        </p>
                        {(() => {
                          type RuntimeView = {
                            models?: {
                              ocr?: { label?: string; chatModel?: string; chatBaseUrl?: string };
                              translation?: {
                                label?: string;
                                chatModel?: string;
                                chatBaseUrl?: string;
                                embeddingsModel?: string;
                              };
                            };
                            prompts?: { ocr?: string; translation?: string };
                          };

                          const runtime = getRuntime(job) as RuntimeView | null;
                          const models = runtime?.models;
                          const prompts = runtime?.prompts;

                          const ocr = models?.ocr;
                          const tr = models?.translation;

                          if (!runtime) {
                            return (
                              <p className="text-sm text-gray-500 dark:text-gray-300">
                                Пока нет данных (появится в новых задачах / после retry).
                              </p>
                            );
                          }

                          return (
                            <div className="space-y-3 text-xs">
                              <div>
                                <p className="text-gray-500 dark:text-gray-300 mb-1">OCR</p>
                                <div className="text-gray-900 dark:text-white">
                                  <div><span className="font-medium">Label:</span> {ocr?.label ?? '—'}</div>
                                  <div><span className="font-medium">Model:</span> {ocr?.chatModel ?? '—'}</div>
                                  <div><span className="font-medium">Base URL:</span> {ocr?.chatBaseUrl ?? '—'}</div>
                                  <div><span className="font-medium">Prompt:</span> {prompts?.ocr ?? '—'}</div>
                                </div>
                              </div>
                              <div>
                                <p className="text-gray-500 dark:text-gray-300 mb-1">Translation</p>
                                <div className="text-gray-900 dark:text-white">
                                  <div><span className="font-medium">Label:</span> {tr?.label ?? '—'}</div>
                                  <div><span className="font-medium">Model:</span> {tr?.chatModel ?? '—'}</div>
                                  <div><span className="font-medium">Base URL:</span> {tr?.chatBaseUrl ?? '—'}</div>
                                  <div><span className="font-medium">Embeddings:</span> {tr?.embeddingsModel ?? '—'}</div>
                                  <div><span className="font-medium">Prompt:</span> {prompts?.translation ?? '—'}</div>
                                </div>
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                      )}
                    </div>

                    {isAdmin && (
                    <details className="mt-3">
                      <summary className="cursor-pointer text-xs text-gray-600 dark:text-gray-300">
                        Показать сырой JSON прогресса
                      </summary>
                      <pre className="mt-2 bg-gray-50 dark:bg-gray-700 p-4 rounded-lg overflow-auto text-xs">
                        {JSON.stringify(job.progress, null, 2)}
                      </pre>
                    </details>
                    )}
                  </div>
                )}

                {auditLoading ? (
                  <div className="text-sm text-gray-500 dark:text-gray-300">Загружаем историю действий…</div>
                ) : auditEvents && auditEvents.filter((e) => e.action !== 'job.status_changed').length > 0 ? (
                  <details className="mt-2">
                    <summary className="cursor-pointer text-sm text-gray-700 dark:text-gray-200">
                      История действий ({auditEvents.filter((e) => e.action !== 'job.status_changed').length})
                    </summary>
                    <div className="mt-3 space-y-2">
                      {auditEvents
                        .filter((e) => e.action !== 'job.status_changed')
                        .map((e) => (
                          <div
                            key={e.id}
                            className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg flex items-start justify-between gap-4"
                          >
                            <div className="min-w-0">
                              <div className="text-sm text-gray-900 dark:text-white">
                                {getAuditLabel(e)}
                              </div>
                              <div className="text-xs text-gray-500 dark:text-gray-300 mt-1">
                                {getActorLabel(e)}
                                {' • '}
                                {new Date(e.created_at).toLocaleString('ru-RU')}
                              </div>
                            </div>
                          </div>
                        ))}
                    </div>
                  </details>
                ) : (
                  <div className="text-sm text-gray-500 dark:text-gray-300">
                    История действий пока пуста.
                  </div>
                )}
              </div>
            </Card>
          ) : null}
        </>
      )}

      {!searchJobId && (
        <>
          {/* Status filter tabs */}
          <div className="flex flex-wrap gap-2 mb-4">
            {statusTabs.map(tab => {
              const count = tab.value ? (statusCounts[tab.value] ?? 0) : (allJobs?.length ?? 0);
              const isActive = filterStatus === tab.value;
              return (
                <button
                  key={tab.value ?? 'all'}
                  onClick={() => setFilterStatus(tab.value)}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                    isActive
                      ? `${tab.color} ring-2 ring-offset-1 ring-primary-500 dark:ring-offset-gray-900`
                      : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  {tab.label}
                  {count > 0 && (
                    <span className={`ml-1.5 px-1.5 py-0.5 rounded-full text-xs ${
                      isActive ? 'bg-white/30 dark:bg-black/20' : 'bg-gray-200 dark:bg-gray-600'
                    }`}>
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Batch controls */}
          <div className="flex flex-wrap items-center gap-2 mb-4">
            {filteredJobs.length > 0 && (
              <Button variant="secondary" size="sm" onClick={toggleSelectAll}>
                {selectedJobs.size === filteredJobs.length ? <CheckSquare className="h-4 w-4 mr-1" /> : <Square className="h-4 w-4 mr-1" />}
                {selectedJobs.size === filteredJobs.length ? 'Снять выбор' : 'Выбрать все'}
              </Button>
            )}
            {selectedJobs.size > 0 && (
              <>
                <span className="text-sm text-gray-500 dark:text-gray-400">Выбрано: {selectedJobs.size}</span>
                <Button size="sm" onClick={() => handleBatchAction('retry')} disabled={batchMutation.isPending}>
                  <RotateCcw className="h-4 w-4 mr-1" />Retry
                </Button>
                <Button size="sm" variant="secondary" onClick={() => handleBatchAction('pause')} disabled={batchMutation.isPending}>
                  <Pause className="h-4 w-4 mr-1" />Пауза
                </Button>
                <Button size="sm" variant="secondary" onClick={() => handleBatchAction('resume')} disabled={batchMutation.isPending}>
                  <Play className="h-4 w-4 mr-1" />Возобновить
                </Button>
                <Button size="sm" variant="secondary" onClick={() => handleBatchAction('remove')} disabled={batchMutation.isPending}>
                  <Trash2 className="h-4 w-4 mr-1" />Удалить
                </Button>
              </>
            )}
            {failedJobs.length > 0 && selectedJobs.size === 0 && (
              <Button variant="secondary" size="sm" onClick={handleRetryAllFailed} disabled={batchMutation.isPending}>
                <RotateCcw className="h-4 w-4 mr-1" />
                Повторить все с ошибкой ({failedJobs.length})
              </Button>
            )}
          </div>

          <Card title="Все задачи">
            {allJobsLoading ? (
              <LoadingSpinner />
            ) : filteredJobs.length > 0 ? (
              <div className="space-y-2">
                {filteredJobs.map((j) => (
                  <div
                    key={j.id}
                    className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3 flex-1">
                        <input
                          type="checkbox"
                          checked={selectedJobs.has(j.id)}
                          onChange={() => toggleJobSelection(j.id)}
                          className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 dark:border-gray-500 dark:bg-gray-600"
                        />
                        {getStatusIcon(j.status)}
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <Link
                              to={`/jobs/${j.id}`}
                              className="font-medium text-gray-900 dark:text-white hover:text-primary-600 dark:hover:text-primary-400"
                            >
                              {getSeriesTitle(j)}
                              {getChapterNo(j) ? ` — Глава ${getChapterNo(j)}` : ''}
                            </Link>
                            <span className="text-sm text-gray-500 dark:text-gray-400">
                              {getStatusLabel(j.status)}
                            </span>
                          </div>
                          <div className="flex flex-wrap items-center gap-x-2 text-xs text-gray-500 dark:text-gray-400 mt-1">
                            <span>{formatRelativeTime(j.created_at)}</span>
                            <span>•</span>
                            <span className="font-mono">{j.id.slice(0, 8)}…</span>
                            {j.created_by_username && (
                              <>
                                <span>•</span>
                                <UserBadge username={j.created_by_username} showIcon={false} />
                              </>
                            )}
                          </div>
                          {(j.status === 'processing' || j.status === 'queued') && (
                            <div className="mt-1.5 max-w-xs">
                              <JobProgressBar job={j} compact />
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        {(j.status === 'processing' || j.status === 'queued') && (
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => {
                              if (window.confirm('Приостановить задачу?')) {
                                pauseMutation.mutate(j.id);
                              }
                            }}
                            disabled={pauseMutation.isPending}
                            title="Приостановить"
                          >
                            <Pause className="h-4 w-4" />
                          </Button>
                        )}
                        {j.status === 'paused' && (
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => {
                              resumeMutation.mutate(j.id);
                            }}
                            disabled={resumeMutation.isPending}
                            title="Возобновить"
                          >
                            <Play className="h-4 w-4" />
                          </Button>
                        )}
                        {(j.status === 'processing' || j.status === 'queued' || j.status === 'paused') && (
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => {
                              if (window.confirm('Удалить задачу? Это действие нельзя отменить.')) {
                                removeMutation.mutate(j.id);
                              }
                            }}
                            disabled={removeMutation.isPending}
                            title="Удалить"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                        {j.status === 'failed' && (
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => handleRetry(j.id)}
                            disabled={retryMutation.isPending}
                            title="Перезапустить"
                          >
                            <RotateCcw className="h-4 w-4" />
                          </Button>
                        )}
                        {j.status === 'completed' && hasOutputFiles(j) && (
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => download('txt', j.id)}
                            disabled={!!downloading}
                            title="Скачать TXT"
                          >
                            <Download className="h-4 w-4" />
                          </Button>
                        )}
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => {
                            navigate(`/jobs/${j.id}`);
                          }}
                        >
                          Открыть
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <p className="text-gray-500 dark:text-gray-400">
                  Задачи не найдены
                </p>
              </div>
            )}
          </Card>
          <Card>
            <div className="text-center py-12">
              <p className="text-gray-500 dark:text-gray-400">
                Введите ID задачи для просмотра статуса
              </p>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}
